/* MbuziPlus Course certificate (PDF). The student's own name sits on the signature line. */

export const CERT_PARTNERS = [
  { key: "mbuziplus", label: "Imfuyo MbuziPlus", url: "/certificate/imfuyo-mbuziplus.jpg" },
  { key: "foundation", label: "Imfuyo Foundation", url: "/certificate/imfuyo-foundation.jpg" },
  // Billy's and Charis Farm logos will be added once confirmed.
];

/* Fetch one image and return { data, w, h, fmt } or null. Never throws. */
async function loadImage(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const data = await new Promise((ok, no) => {
      const r = new FileReader();
      r.onload = () => ok(r.result);
      r.onerror = no;
      r.readAsDataURL(blob);
    });
    const dim = await new Promise((ok, no) => {
      const im = new Image();
      im.onload = () => ok({ w: im.naturalWidth, h: im.naturalHeight });
      im.onerror = no;
      im.src = data;
    });
    return { data, ...dim, fmt: /png/i.test(blob.type) ? "PNG" : "JPEG" };
  } catch {
    return null;
  }
}

export async function loadCertificateLogos() {
  const out = {};
  await Promise.all(CERT_PARTNERS.map(async (p) => { out[p.key] = await loadImage(p.url); }));
  return out;
}

const GREEN = [40, 80, 32];
const MID = [62, 122, 50];
const GOLD = [176, 138, 52];
const INK = [13, 31, 22];
const GREY = [90, 90, 90];

/* Draws the certificate onto a jsPDF document that is passed in, so it can be tested anywhere. */
export function drawCertificate(pdf, { name, percent, score, total, date, certId, topics, logos = {}, watermark = "" }) {
  const W = 297, H = 210, cx = W / 2;
  const spaced = (t, y, cs) => pdf.text(t, cx + cs / 2, y, { align: "center", charSpace: cs }); // keeps spaced capitals centred
  const diamondRule = (y, half) => {
    pdf.setDrawColor(...GOLD); pdf.setLineWidth(0.4);
    pdf.line(cx - half, y, cx - 5, y); pdf.line(cx + 5, y, cx + half, y);
    pdf.setFillColor(...GOLD); pdf.triangle(cx - 2.6, y, cx, y - 2.6, cx + 2.6, y, "F"); pdf.triangle(cx - 2.6, y, cx, y + 2.6, cx + 2.6, y, "F");
  };

  // page, frame, gold inner rule, corner triangles
  pdf.setFillColor(255, 255, 255); pdf.rect(0, 0, W, H, "F");
  pdf.setFillColor(...GREEN); pdf.rect(0, 0, W, H, "F");
  pdf.setFillColor(255, 255, 255); pdf.rect(7, 7, W - 14, H - 14, "F");
  pdf.setDrawColor(...GOLD); pdf.setLineWidth(0.6); pdf.rect(10.5, 10.5, W - 21, H - 21);
  pdf.setLineWidth(0.25); pdf.rect(13, 13, W - 26, H - 26);
  pdf.setFillColor(...GREEN);
  pdf.triangle(7, 7, 37, 7, 7, 37, "F"); pdf.triangle(W - 7, 7, W - 37, 7, W - 7, 37, "F");
  pdf.triangle(7, H - 7, 37, H - 7, 7, H - 37, "F"); pdf.triangle(W - 7, H - 7, W - 37, H - 7, W - 7, H - 37, "F");
  pdf.setFillColor(...GOLD);
  pdf.triangle(7, 7, 22, 7, 7, 22, "F"); pdf.triangle(W - 7, 7, W - 22, 7, W - 7, 22, "F");
  pdf.triangle(7, H - 7, 22, H - 7, 7, H - 22, "F"); pdf.triangle(W - 7, H - 7, W - 22, H - 7, W - 7, H - 22, "F");

  // header: academy name at far left, partner logos at far right
  const hy = 20;
  pdf.setTextColor(...GREEN); pdf.setFont("helvetica", "bold"); pdf.setFontSize(26);
  pdf.text("IMFUYO", 28, hy + 12);
  pdf.text("ACADEMY", 28, hy + 24);
  pdf.setDrawColor(...GOLD); pdf.setLineWidth(0.8); pdf.line(28, hy + 28.5, 62, hy + 28.5);
  pdf.setTextColor(...MID); pdf.setFontSize(8.5);
  pdf.text("MBUZIPLUS COURSE", 28.2, hy + 34, { charSpace: 1.4 });
  const logoH = 36, logoW = 36, lgap = 8;
  CERT_PARTNERS.forEach((p, k) => {
    const x = W - 28 - (CERT_PARTNERS.length - k) * logoW - (CERT_PARTNERS.length - 1 - k) * lgap;
    const img = logos[p.key];
    if (img && img.data) {
      const r = Math.min(logoW / img.w, logoH / img.h), w = img.w * r, h = img.h * r;
      try { pdf.addImage(img.data, img.fmt, x + (logoW - w) / 2, hy + (logoH - h) / 2, w, h); return; } catch { /* wordmark below */ }
    }
    pdf.setTextColor(...GREEN); pdf.setFont("helvetica", "bold"); pdf.setFontSize(11);
    pdf.text(pdf.splitTextToSize(p.label, logoW), x + logoW / 2, hy + logoH / 2, { align: "center" });
  });

  // heading block
  diamondRule(63, 100);
  pdf.setTextColor(...GREEN); pdf.setFont("times", "bold"); pdf.setFontSize(38);
  pdf.text("Certificate of Completion", cx, 80, { align: "center" });
  pdf.setTextColor(...GREY); pdf.setFont("times", "italic"); pdf.setFontSize(13);
  pdf.text("This is to certify that", cx, 91, { align: "center" });

  // student's name on a soft band
  pdf.setFillColor(238, 246, 234); pdf.rect(30, 95, W - 60, 22, "F");
  let size = 34; pdf.setFont("times", "bolditalic"); pdf.setFontSize(size);
  while (pdf.getTextWidth(name) > W - 90 && size > 16) { size -= 2; pdf.setFontSize(size); }
  pdf.setTextColor(...INK); pdf.text(name, cx, 110, { align: "center" });
  pdf.setDrawColor(...GOLD); pdf.setLineWidth(0.5); pdf.line(30, 95, W - 30, 95); pdf.line(30, 117, W - 30, 117);

  // body
  pdf.setTextColor(...GREY); pdf.setFont("helvetica", "normal"); pdf.setFontSize(12);
  pdf.text("a MbuziPlus Student, has successfully completed the", cx, 127, { align: "center" });
  pdf.setFont("times", "bold"); pdf.setFontSize(20); pdf.setTextColor(...GREEN);
  pdf.text("MbuziPlus Course by Imfuyo Academy", cx, 137, { align: "center" });
  let my = 148;
  if (score != null && total) {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11.5); pdf.setTextColor(...GREY);
    pdf.text(`and passed the final examination with a score of ${score} out of ${total} (${percent}%).`, cx, 146, { align: "center" });
    my = 155;
  }
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(...MID);
  pdf.text("Dairy goat farming, from your first goat to your first pay day", cx, my, { align: "center" });

  // bottom: student's signature line | seal | founder's signature
  const baseY = 178;
  pdf.setDrawColor(...GREY); pdf.setLineWidth(0.3);
  pdf.line(40, baseY, 112, baseY); pdf.line(W - 112, baseY, W - 40, baseY);
  let ns = 17; pdf.setFont("times", "bolditalic"); pdf.setFontSize(ns);
  while (pdf.getTextWidth(name) > 68 && ns > 9) { ns -= 1; pdf.setFontSize(ns); }
  pdf.setTextColor(...INK); pdf.text(name, 76, baseY - 2.5, { align: "center" });
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...GREY);
  pdf.text("MbuziPlus Student", 76, baseY + 5, { align: "center" });
  pdf.setFont("times", "bolditalic"); pdf.setFontSize(17); pdf.setTextColor(...INK);
  pdf.text("Bernard Wanjohi Njathi", W - 76, baseY - 2.5, { align: "center" });
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...GREY);
  pdf.text("Founder and Chief Executive Officer, Imfuyo", W - 76, baseY + 5, { align: "center" });

  // footer
  pdf.setTextColor(...GREY); pdf.setFontSize(8.5);
  pdf.text(`Date of issue ${date}   |   Certificate number ${certId}   |   www.imfuyo.farm/academy`, cx, 193, { align: "center" });

  // optional diagonal watermark (used for shareable drafts)
  if (watermark) {
    pdf.saveGraphicsState();
    pdf.setGState(new pdf.GState({ opacity: 0.13 }));
    pdf.setTextColor(...GREEN); pdf.setFont("helvetica", "bold"); pdf.setFontSize(120);
    pdf.text(watermark, cx, 128, { align: "center", angle: 30 });
    pdf.restoreGraphicsState();
  }
}

export async function makeCertificate(opts) {
  const { jsPDF } = await import("jspdf");
  const logos = opts.logos || (await loadCertificateLogos());
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  drawCertificate(pdf, { ...opts, logos });
  pdf.save(`MbuziPlus-Certificate-${opts.name.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`);
}
